import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isFunction } from 'lodash';
import { fieldProps, getPreferredProp } from '../utils';

function CheckBoxField(props) {
  const {
    style,
    disabled,
    hideLabel,
    hint,
    id,
    label,
    labelPosition,
    showError,
    showHint,
    required,
  } = props;

  const onChange = (e) => {
    if (!disabled) {
      document.getElementById(id).focus();

      const value = e.target.checked;
      const callback = getPreferredProp(props, 'onChange');

      if (isFunction(callback)) {
        callback(e);
      }
      if (isFunction(props.onChange) && props.onChange !== callback) {
        props.onChange(value);
      }
    }
  };

  const checked = getPreferredProp(props, 'value') || getPreferredProp(props, 'checked');
  const hasError = getPreferredProp(props, 'invalid') && getPreferredProp(props, 'touched');
  const error = getPreferredProp(props, 'error') || (required ? 'Required' : '');

  const inputLabelWrapperClass = classNames('flex gap-2 items-center', {
    'flex-row-reverse': labelPosition === 'left',
    'content-end': labelPosition === 'left',
    'opacity-50': disabled,
  });

  const inputClass = classNames(
    'appearance-none border w-4 h-4 focus:outline-solid-offset rounded relative shrink-0 checked:bg-action-blue checked:border-action-blue', {
      'border-medium-grey': !hasError,
      'border-red': hasError,
      'hover:bg-medium-fill-grey cursor-pointer': !disabled,
    }, style.input);

  const labelClass = classNames(
    'text-sm text-text-blue font-regular-font tracking-normal', {
      'text-right': labelPosition === 'left',
      'sr-only': hideLabel,
      'cursor-pointer': !disabled,
    },
    style.label);

  const svgClass = classNames('absolute w-4 h-4 rounded', {
    'hover:bg-deep-blue cursor-pointer': !disabled,
  });

  const hintClass = classNames(
    'text-xs italic leading-4 text-medium-grey pt-1', {
      'opacity-50': disabled,
      'text-right': labelPosition === 'left',
    },
    style.hint);

  const errorClass = classNames(
    'pt-1 text-red text-xs', {
      'text-right': labelPosition === 'left',
    },
    style.error);

  return (
    <div
      className={classNames('uu-checkbox-field', style.container)}
      id={`${id}-uu-checkbox-field__container`}
      data-testid={`${id}-uu-checkbox-field__container`}
    >
      <div className="w-auto inline-block">
        <div className={inputLabelWrapperClass}>
          <input
            id={id}
            data-testid={`${id}-uu-checkbox-field__input`}
            type="checkbox"
            checked={checked}
            className={inputClass}
            onChange={onChange}
            onBlur={getPreferredProp(props, 'onBlur')}
            onFocus={getPreferredProp(props, 'onFocus')}
            name={getPreferredProp(props, 'name')}
            disabled={disabled}
          />
          <label
            htmlFor={id}
            className={labelClass}
            id={`${id}-uu-checkbox-field__label`}
            data-testid={`${id}-uu-checkbox-field__label`}
            aria-label={`checkbox for ${label}${showHint && hint ? `, hint: ${hint}` : ''} ${showError && hasError ? `, error: ${error}` : ''}`}
          >
            {label}
          </label>
          {
            checked && (
              <svg
                id={`${id}-uu-checkbox-field__check`}
                data-testid={`${id}-uu-checkbox-field__check`}
                className={svgClass}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                onClick={onChange}
              >
                <polyline points="20 6 9 17 4 12" fill="none" />
              </svg>
            )
          }
        </div>
        {
          showHint && hint && (
            <div
              className={hintClass}
              id={`${id}-uu-checkbox-field__hint`}
              data-testid={`${id}-uu-checkbox-field__hint`}
            >
              {hint}
            </div>
          )
        }
        {
          showError && hasError && (
            <div
              className={errorClass}
              id={`${id}-uu-checkbox-field__error`}
              data-testid={`${id}-uu-checkbox-field__error`}
            >
              {error}
            </div>
          )
        }
      </div>
    </div>
  );
}

CheckBoxField.propTypes = {
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** is checked or not */
  checked: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
  /** Override the inline-styles of the elements with tailwind style || **Not Recommended** */
  style: PropTypes.shape({
    container: PropTypes.string,
    button: PropTypes.string,
    hint: PropTypes.string,
    label: PropTypes.string,
    toggle: PropTypes.string,
    wrapper: PropTypes.string,
  }),
  /** Should the label be hidden? */
  hideLabel: PropTypes.bool,
  /** label text for button */
  label: PropTypes.string.isRequired,
  /** Position of the label */
  labelPosition: PropTypes.oneOf(['right', 'left']),
  /** Is the input disabled? */
  disabled: PropTypes.bool,
  /** Help text displayed below the input */
  hint: PropTypes.node,
  /** Show Error HTML defaults to true */
  showError: PropTypes.bool,
  /** Show Hint HTML defaults to true */
  showHint: PropTypes.bool,
  /** is required or not */
  required: PropTypes.bool,
  /** See fieldProps doc */
  ...fieldProps,
};

CheckBoxField.defaultProps = {
  checked: false,
  disabled: false,
  hideLabel: false,
  hint: '',
  labelPosition: 'right',
  showError: true,
  showHint: true,
  required: false,
  style: {
    container: '',
    label: '',
    input: '',
    hint: '',
    error: '',
  },
};

export default CheckBoxField;
